import React, { createContext, useCallback, useContext, useRef, ReactNode } from "react";

type AudioEffectOptions = {
  volume?: number;
  playbackRate?: number;
  inBackground?: boolean;
  loop?: boolean;
};

type AudioEffectsContextType = {
  playEffect: (src: string, options?: AudioEffectOptions) => void;
  stopEffect: (options?: AudioEffectOptions) => void;
};

const AudioEffectsContext = createContext<AudioEffectsContextType | undefined>(
  undefined,
);

export const AudioEffectsProvider = ({ children }: { children: ReactNode }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const backgroundAudioRef = useRef<HTMLAudioElement>(null);

  const stopEffect = useCallback((options: AudioEffectOptions = {}) => {
    if (options.inBackground && backgroundAudioRef.current) {
      backgroundAudioRef.current.pause();
    }
  }, []);

  const playEffect = useCallback((src: string, options: AudioEffectOptions = {}) => {
    if (!audioRef.current) return;

    if (options.inBackground && backgroundAudioRef?.current) {
      const audio = backgroundAudioRef.current;
      audio.src = src;
      audio.volume = options.volume ?? 0.2;
      audio.playbackRate = options.playbackRate ?? 1;
      audio.loop = options.loop ?? false;

      audio.currentTime = 0; // restart from beginning
      audio.play().catch((error) => console.error("Audio play error:", error));
      return;
    }

    const audio = audioRef.current;
    audio.src = src;
    audio.volume = options.volume ?? 0.2;
    audio.playbackRate = options.playbackRate ?? 1;

    audio.currentTime = 0; // restart from beginning
    audio.play().catch((error) => console.error("Audio play error:", error));
  }, []);

  return (
    <AudioEffectsContext.Provider value={{ playEffect, stopEffect }}>
      {children}
      {/* Pre-rendered <audio> element for instant playback */}
      <audio ref={audioRef} />
      <audio ref={backgroundAudioRef} />
    </AudioEffectsContext.Provider>
  );
};

export const useAudioEffects = () => {
  const context = useContext(AudioEffectsContext);

  if (!context) {
    throw new Error("useAudioEffects must be used inside AudioEffectsProvider");
  }
  return context;
};
